import React, { ReactElement, useState } from 'react';
import { UnmountClosed } from 'react-collapse';
import { formatMoney } from '~/helpers/currency';
import Header from '../Header';
import Auction from '~/App/shared/interfaces/Auction';
import ReleasingInformation from './ReleasingInformation';
import { useTranslation } from '~/Locale';
import AdditionalServicesLoader from '../AdditionalServicesLoader';
import { ReleasingIcon } from '@kvdbil/components';
import { ServiceContainer } from '../..';
import { CMSDataObjectPageService } from '~/App/views/ObjectPage/cmsDataHandlers';

export interface ReleasingObjectInfo {
  price: number | null;
  initialMonthlyReleasingCost: number | null;
  registrationPlate: string;
  updateHOC: (cost: number | null) => void;

  setReleasingInformation?(releasing: Record<string, unknown>): void;
}

interface Props {
  auction: Auction;
  isBuyNow: boolean;
  cmsData?: CMSDataObjectPageService;
  registrationPlate: string;
  showReleasingForm: boolean;
  initialMonthlyReleasingCost: number;

  setReleasingInformation?(releasing: Record<string, unknown>): void;
}

const ReleasingService = ({
  auction,
  isBuyNow,
  cmsData,
  registrationPlate,
  initialMonthlyReleasingCost,
  showReleasingForm,
  setReleasingInformation
}: Props): ReactElement => {
  const { t } = useTranslation();
  const [isExpanded, setIsExpanded] = useState(false);
  const [releasingCost, setReleasingCost] = useState<number | null>(null);

  const toggleExpanded = () => setIsExpanded(!isExpanded);

  const initialPrice = isBuyNow
    ? auction?.buyNowAmount
    : auction?.activeAuction?.highestBid?.amount || auction?.startBid;

  if (!cmsData) {
    return <AdditionalServicesLoader service="Releasing" />;
  }

  const objectInfo = {
    price: initialPrice,
    initialMonthlyReleasingCost,
    registrationPlate,
    updateHOC: setReleasingCost,
    setReleaseInformation: setReleasingInformation
  };
  const ariaControls = 'additional-service-releasing-content';

  return (
    <ServiceContainer id="releasingCalculator">
      <Header
        isExpanded={isExpanded}
        toggleExpanded={toggleExpanded}
        title={t('LEASING_CALCULATOR_HEADING')}
        icon={cmsData?.icon ?? ReleasingIcon}
        responsiveImage={cmsData?.image?.responsiveImage}
        description={
          releasingCost && releasingCost >= 500
            ? t(
                releasingCost
                  ? 'Monthly cost bidding view %1$s'
                  : 'From monthly cost bidding view %1$s',
                formatMoney({
                  value: releasingCost || initialMonthlyReleasingCost,
                  currency: t('SEK')
                })
              )
            : t('Calculate releasing cost')
        }
        ariaControls={ariaControls}
      />

      <UnmountClosed isOpened={isExpanded}>
        <ReleasingInformation
          id={ariaControls}
          objectInfo={objectInfo}
          isBuyNow={isBuyNow}
          isReleasingManualCheckout={false}
          cmsData={cmsData}
          showReleasingForm={showReleasingForm}
        />
      </UnmountClosed>
    </ServiceContainer>
  );
};

export default ReleasingService;
